import React, { useState, useEffect } from 'react';

const hexToRgb = (hex) => hex.replace('#', '').match(/.{2}/g).map(h => parseInt(h, 16));

const luminance = (hex) => {
  const [r, g, b] = hexToRgb(hex).map(v => v / 255);
  const lin = v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

const lighten = (hex, amt) => {
  const [r, g, b] = hexToRgb(hex);
  return '#' + [r, g, b].map(c => Math.min(255, Math.round(c + (255 - c) * amt)).toString(16).padStart(2, '0')).join('');
};

const darken = (hex, amt) => {
  const [r, g, b] = hexToRgb(hex);
  return '#' + [r, g, b].map(c => Math.max(0, Math.round(c * (1 - amt))).toString(16).padStart(2, '0')).join('');
};

const CARD_CSS = `
  .dc-widget { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; border-radius: 10px; overflow: hidden; display: flex; flex-direction: column; box-sizing: border-box; }
  .dc-widget * { box-sizing: border-box; }

  /* Header */
  .dc-header { display: flex; align-items: center; gap: 10px; padding: 14px 16px 10px; border-bottom: 1px solid #f0f0f0; }
  .dc-widget.dark .dc-header { border-color: #2a2f2b; }
  .dc-hd-icon { width: 30px; height: 30px; border-radius: 9px; background: var(--w-accent); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .dc-hd-icon svg { width: 15px; height: 15px; stroke: #fff; stroke-width: 2.2; fill: none; stroke-linecap: round; stroke-linejoin: round; }
  .dc-hd-title { font-size: 14px; font-weight: 800; letter-spacing: -0.01em; color: #1a1a2e; }
  .dc-widget.dark .dc-hd-title { color: #F0F4F1; }

  /* Body */
  .dc-body { padding: 10px 16px 14px; }
  .dc-desc { font-size: 12.5px; color: #888; line-height: 1.55; margin-top: 8px; margin-bottom: 14px; padding-bottom: 2px; }
  .dc-widget.dark .dc-desc { color: #9CA3AF; }

  /* Amounts row */
  .dc-amounts { display: flex; align-items: baseline; gap: 6px; margin-bottom: 8px; }
  .dc-raised { font-size: 24px; font-weight: 800; letter-spacing: -0.03em; color: #1a1a2e; }
  .dc-widget.dark .dc-raised { color: #F0F4F1; }
  .dc-goal-label { font-size: 12px; color: #888; font-weight: 500; }
  .dc-widget.dark .dc-goal-label { color: #9CA3AF; }

  /* Progress bar */
  .dc-bar-track { height: 8px; border-radius: 99px; background: #e8eaed; overflow: hidden; margin-bottom: 8px; }
  .dc-widget.dark .dc-bar-track { background: #2a2f2b; }
  .dc-bar-fill { height: 100%; border-radius: 99px; background: var(--w-accent); transition: width 1.1s cubic-bezier(.4,0,.2,1); }

  /* Pct row */
  .dc-pct-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
  .dc-pct { font-size: 11px; font-weight: 700; color: var(--w-accent); }
  .dc-goal-amt { font-size: 11px; color: #aaa; font-weight: 500; }

  /* Meta: donors */
  .dc-meta { display: flex; gap: 16px; margin-bottom: 16px; }
  .dc-meta-item { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #888; }
  .dc-widget.dark .dc-meta-item { color: #9CA3AF; }
  .dc-meta-item svg { width: 13px; height: 13px; stroke: currentColor; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }
  .dc-meta-item strong { color: #1a1a2e; font-weight: 700; }
  .dc-widget.dark .dc-meta-item strong { color: #D1D5DB; }

  /* Donate button */
  .dc-btn { display: flex; align-items: center; justify-content: center; gap: 7px; width: 100%; padding: 11px 16px; background: var(--w-accent); border-radius: 8px; color: #fff; font-size: 13.5px; font-weight: 700; text-decoration: none; border: none; cursor: pointer; letter-spacing: -0.01em; box-shadow: 0 4px 14px rgba(0,0,0,0.12); }
  .dc-btn svg { width: 14px; height: 14px; stroke: currentColor; stroke-width: 2.5; fill: none; stroke-linecap: round; stroke-linejoin: round; }

  /* Powered */
  .dc-powered { display: block; text-align: center; padding: 10px; font-size: 9px; color: #aaa; border-top: 1px solid #f0f0f0; text-decoration: none; }
  .dc-widget.dark .dc-powered { border-color: #2a2f2b; color: #4B5563; }
  .dc-powered strong { color: #888; font-weight: 700; font-size: 10px;}
  .dc-widget.dark .dc-powered strong { color: #6B7280; }

  /* ── Size variants ── */
  /* Small: compact, no description/meta */
  .dc-small { max-width: 320px; width: 100%; background: #ffffff; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
  .dc-small.dark { background: #1F2937; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
  .dc-small .dc-header { padding: 10px 14px 8px; }
  .dc-small .dc-hd-icon { width: 24px; height: 24px; border-radius: 7px; }
  .dc-small .dc-hd-icon svg { width: 12px; height: 12px; }
  .dc-small .dc-hd-title { font-size: 12.5px; }
  .dc-small .dc-body { padding: 12px 14px 10px; }
  .dc-small .dc-desc { display: none; }
  .dc-small .dc-raised { font-size: 18px; }
  .dc-small .dc-bar-track { height: 6px; margin-bottom: 6px; }
  .dc-small .dc-meta { display: none; }
  .dc-small .dc-btn { padding: 9px 14px; font-size: 12.5px; border-radius: 6px; }
  .dc-small .dc-pct-row { margin-bottom: 10px; }

  /* Medium */
  .dc-medium { max-width: 380px; width: 100%; background: #ffffff; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
  .dc-medium.dark { background: #1F2937; box-shadow: 0 1px 4px rgba(0,0,0,0.3); }

  /* Large: full card with hover */
  .dc-large { max-width: 400px; width: 100%; background: #ffffff; box-shadow: 0 2px 8px rgba(0,0,0,0.05); transition: box-shadow .2s, transform .2s; }
  .dc-large:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); transform: translateY(-1px); }
  .dc-large.dark { background: #1F2937; box-shadow: 0 2px 8px rgba(0,0,0,0.3); }
  .dc-large.dark:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.4); }
  .dc-large .dc-raised { font-size: 28px; }
  .dc-large .dc-bar-track { height: 10px; }
  .dc-large .dc-btn { padding: 13px 16px; font-size: 14.5px; }

   /* -- Animations -- */
  @keyframes dc-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .dc-small .dc-bar-fill.animated, .dc-medium .dc-bar-fill.animated { background: linear-gradient(90deg, var(--w-accent) 30%, var(--w-accent-light) 50%, var(--w-accent) 70%); background-size: 200% 100%; animation: dc-shimmer 6s ease-in-out infinite; }
  .dc-large .dc-bar-fill.animated { background: linear-gradient(90deg, var(--w-accent) 30%, var(--w-accent-light) 50%, var(--w-accent-dark) 70%); background-size: 200% 100%; animation: dc-shimmer 6s ease-in-out infinite; }
`;

const HeartIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);

const DonationCardWidget = ({
  size = 'medium',
  campaign = 'Emergency Relief Fund',
  description = 'Help us expand our prayer hall to accommodate our growing community.',
  raised = '$12,450',
  goal = '$20,000',
  pct = 62,
  donations = 284,
  btnText = 'Donate Now',
  
  accentColor = '#0047AB',
  outline = 1,
  darkMode = false,
  
  showDescription = true,
  showDonations = true,
  showDonateBtn = true,
}) => {
  const [fillWidth, setFillWidth] = useState(0);
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    let styleEl = document.getElementById('dc-original-styles');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'dc-original-styles';
      styleEl.innerHTML = CARD_CSS;
      document.head.appendChild(styleEl);
    }
    setIsAnimated(false);

    const timer = setTimeout(() => setFillWidth(pct), 50);
    const timer2 = setTimeout(() => setIsAnimated(true), 1400);
    return () => { clearTimeout(timer);  clearTimeout(timer2);}
  }, [pct]);

  const accentLight = lighten(accentColor, 0.9);
  const accentDark = darken(accentColor, 0.2);
  
  const getOutlineStyle = () => {
    if (outline === 0) return { border: 'none' };
    if (outline === 1) return { border: `1px solid ${darkMode ? '#333' : '#e8eaed'}` };
    return { border: `${outline}px solid ${accentColor}` };
  };

  const widgetStyles = {
    ...getOutlineStyle(),
    '--w-accent': accentColor,
    '--w-accent-dark': accentDark,
    '--w-accent-light': accentLight,
  };

  const darkClass = darkMode ? ' dark' : '';
  const sizeClass = `dc-${size}`; 

  return (
    <div className={`dc-widget ${sizeClass}${darkClass}`} style={widgetStyles}>
      
      <div className="dc-header">
        <div className="dc-hd-icon"><HeartIcon /></div>
        <span className="dc-hd-title">{campaign}</span>
      </div>
      
      <div className="dc-body">
        
        {showDescription && size !== 'small' && (
          <p className="dc-desc">{description}</p>
        )}
        
        <div className="dc-amounts" style={{marginTop: `${!showDescription || size === 'small' ? '0px' : '22px'}`}}>
          <span className="dc-raised">{raised}</span>
          <span className="dc-goal-label">raised</span>
        </div>
        
        <div className="dc-bar-track">
          <div className={`dc-bar-fill ${isAnimated ? 'animated' : ''}`} style={{ width: `${fillWidth}%` }}></div>
        </div>
        
        <div className="dc-pct-row">
          <span className="dc-pct">{pct}% funded</span>
          <span className="dc-goal-amt">Goal: {goal}</span>
        </div>
        
        {showDonations && size !== 'small' && (
          <div className="dc-meta">
            <div className="dc-meta-item">
              <UsersIcon /> <strong>{donations}</strong> donations
            </div>
          </div>
        )}
        
        {showDonateBtn && (
          <button className="dc-btn" onClick={(e) => e.preventDefault()}>
            <HeartIcon /> <span>{btnText}</span>
          </button>
        )}
        
      </div>

      <a href="https://www.masjidal.com" className="dc-powered" target="_blank" rel="noopener noreferrer">
        Powered by <strong>Masjidal</strong>
      </a>
      
    </div>
  );
};

export default DonationCardWidget;